/**
 * Tiny global store for "is a PSTN call currently active?" plus the last
 * known call context (number/flag/country). Survives navigation away from
 * the dialer and React remounts via sessionStorage.
 *
 * Why: when the user backgrounds the app mid-call (or taps Back), ConnectDialer
 * can unmount and React refs/state are lost. Without persistence the call
 * screen returns showing `+undefined` and a blank flag.
 *
 * Why a custom store and not Context: BottomNav/Header live OUTSIDE the
 * dialer subtree on most routes, so they need a global, lightweight signal
 * without forcing every consumer to wrap in another provider.
 */
import { useSyncExternalStore } from "react";

export interface ActiveCallInfo {
  phoneNumber: string;
  countryName: string;
  countryFlag: string;
  countryCode: string;
  ratePerMinuteCents: number;
}

const STORAGE_KEY = "elefa.activeCall.v1";

type State = {
  active: boolean;
  info: ActiveCallInfo | null;
};

let state: State = load();

function load(): State {
  if (typeof sessionStorage === "undefined") return { active: false, info: null };
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { active: false, info: null };
    const parsed = JSON.parse(raw) as State;
    return { active: !!parsed.active, info: parsed.info ?? null };
  } catch {
    return { active: false, info: null };
  }
}

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota or private-mode: ignore */
  }
}

const listeners = new Set<() => void>();
function emit() {
  listeners.forEach((l) => l());
}

export function setActiveCall(active: boolean, info?: ActiveCallInfo | null) {
  const next: State = {
    active,
    // Keep last info around even after call ends until explicitly cleared,
    // so a brief post-call remount can still render the right number.
    info: info ?? state.info,
  };
  state = next;
  persist();
  emit();
}

export function updateActiveCallInfo(info: ActiveCallInfo) {
  state = { ...state, info };
  persist();
  emit();
}

export function clearActiveCall() {
  state = { active: false, info: null };
  persist();
  emit();
}

export function getActiveCallSnapshot(): State {
  return state;
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useIsCallActive(): boolean {
  return useSyncExternalStore(subscribe, () => state.active, () => false);
}

export function useActiveCallInfo(): ActiveCallInfo | null {
  return useSyncExternalStore(subscribe, () => state.info, () => null);
}
